'use client'

import { clsx } from 'clsx'
import NextLink from 'next/link'
import { type FC, type ReactNode, useCallback, useRef } from 'react'

import { type PimProductV11 } from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../../../../global-config'
import { urlResolverGetPathForPimProduct } from '../../../../../url-handling'
import { type SelectionEntry } from '../../../product-page-context/useProductPageAttributeSelectionControls'
import { useProductPageContext } from '../../../productPageContext'
import { useMobileScrollAttributeIntoViewOnMount } from '../useMobileScrollAttributeIntoViewOnMount'
import { type ScrollToElement } from '../useScrollElementIntoView'

import { productPageGetAttributeSelectionEntryConditions } from './productPageGetAttributeSelectionEntryConditions'

interface ProductPageAttributeSelectorProps {
  className?: string
  error?: boolean
  label: ReactNode
  onScrollIntoView?: ScrollToElement
  selectionEntry: SelectionEntry
}

export type AttributeSelectionEntryVisualConditions = {
  candidateIsNotBuyable: boolean
  candidateVariant?: PimProductV11
  dontSelectCandidateOnClick: boolean
  hasManyVariants: boolean
  hasVariants: boolean
  isSelected: boolean
  notSelectedAndBuyableVariants: boolean
  notSelectedAndNoVariants: boolean
  selectedAndHasVariants: boolean
  selectedButNotBuyable: boolean
  selectedButNoVariants: boolean
  showAsLink: boolean
}

export const ProductPageAttributeSelector: FC<
  ProductPageAttributeSelectorProps
> = ({ className, error, label, onScrollIntoView, selectionEntry }) => {
  const globalConfig = useGlobalConfigContext()
  const { attributeSelectionControls } = useProductPageContext()
  const pillElementRef = useRef(null)

  const conditions =
    productPageGetAttributeSelectionEntryConditions(selectionEntry)
  const {
    candidateIsNotBuyable,
    candidateVariant,
    notSelectedAndBuyableVariants,
    notSelectedAndNoVariants,
    selectedAndHasVariants,
    selectedButNotBuyable,
    selectedButNoVariants,
    showAsLink,
  } = conditions

  useMobileScrollAttributeIntoViewOnMount({
    attributeElement: pillElementRef,
    isVariantSelected: selectedAndHasVariants,
    onScrollIntoView,
  })

  const handleAttributeSelection = useCallback((): void => {
    attributeSelectionControls.handleAttributeSelect(selectionEntry)
  }, [attributeSelectionControls, selectionEntry])

  const pillClassName = clsx(
    'max-w-full shrink-0 truncate rounded-full border border-transparent px-2.5 py-1.5 text-start scrollbar-hide',
    selectedAndHasVariants && 'bg-dark-brand text-light-primary-strong',
    (notSelectedAndNoVariants || candidateIsNotBuyable) &&
      'bg-light-primary-high text-dark-primary-low',
    notSelectedAndBuyableVariants && 'bg-light-primary-low text-dark-brand',
    (selectedButNoVariants || selectedButNotBuyable) &&
      'border-dark-brand bg-light-primary-high text-dark-primary-low',
    error && 'border-dark-error-medium',
  )

  if (showAsLink && candidateVariant) {
    return (
      <NextLink
        className={clsx(pillClassName, className)}
        data-qa-id="offer-attribute-tile"
        href={urlResolverGetPathForPimProduct(globalConfig, candidateVariant)}
        onClick={handleAttributeSelection}
        prefetch={false}
        ref={pillElementRef}
        scroll={false}
        title={selectionEntry.attribute.value}
      >
        {label}
      </NextLink>
    )
  }

  return (
    <button
      className={clsx(pillClassName, className)}
      onClick={handleAttributeSelection}
      ref={pillElementRef}
      title={selectionEntry.attribute.value}
      type="button"
    >
      {label}
    </button>
  )
}
